import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

import { getText, interpolate } from "shared/lib";

import { usePasswordReset } from "shared/apiHooks";

import { Button, Input } from "shared/components";
import { IconSpinner } from "@/components/icons";

export function ForgotPassword() {
  const navigate = useNavigate();

  const emailRef = useRef<HTMLInputElement>(null);

  const [errorMsg, setErrorMsg] = useState<string>("");

  const {
    isPending: passwordResetLoading,
    isSuccess: passwordResetSuccess,
    mutate: passwordReset,
  } = usePasswordReset({
    onError(error) {
      console.error("<AuthPage> usePasswordReset() onError()", error);
      const response = error.response;
      if (response?.data && Array.isArray(response.data["non_field_errors"])) {
        setErrorMsg(response.data["non_field_errors"][0]);
      } else {
        setErrorMsg(
          getText(
            "There was an unexpected error requesting a password reset. Please refresh your browser and try again, or contact support for assistance.",
          ),
        );
      }
    },
    onSuccess() {
      console.log("<AuthPage> usePasswordReset() onSuccess()");
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!emailRef.current?.value) {
      setErrorMsg(getText("Email field must not be blank"));
      return;
    }

    setErrorMsg("");
    passwordReset({ body: { email: emailRef.current?.value } });
  };

  return (
    <div className="flex flex-col p-6">
      <button
        onClick={() => {
          setErrorMsg("");
          navigate("/login");
        }}
        className="text-textLink hover:text-textLinkHover text-bodymd mb-1 flex w-fit flex-row items-center underline"
      >
        <ChevronLeft className="mr-0.5 h-4 w-4 p-[1px]" />
        {getText("Log in")}
      </button>

      <h1 className="text-heading2xl mb-2 text-left font-semibold">
        {!passwordResetSuccess
          ? getText("Forgot your password?")
          : getText("Thank you")}
      </h1>
      <p hidden={passwordResetSuccess} className="text-bodymd mb-6 text-left">
        {getText(
          "Enter your email address and we'll send you a link to reset your password.",
        )}
      </p>
      <p hidden={!passwordResetSuccess} className="text-bodymd mb-6 text-left">
        {interpolate(
          "A link to reset your password has been sent to {{email}}, if there is an existing account with that address.",
          {
            email: (
              <span className="font-semibold">{emailRef.current?.value}</span>
            ),
          },
        )}
      </p>
      {passwordResetSuccess && (
        <Button variant="outline" onClick={() => navigate("/login")}>
          {getText("Back to log in")}
        </Button>
      )}
      {!passwordResetSuccess && (
        <div className="flex">
          <form onSubmit={handleSubmit} className="w-full">
            <div className="flex flex-col gap-1">
              <label
                className="text-headingsm text-textPrimary"
                htmlFor="email"
              >
                {getText("Email address")}
              </label>
              <Input
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodymd rounded-lg p-4"
                disabled={passwordResetLoading}
                id="email"
                ref={emailRef}
                type="email"
              />
            </div>
            <button
              className="bg-actionDefault text-textWhite text-bodylg mt-4 flex w-full flex-row items-center justify-center rounded-sm px-4 py-2 font-semibold"
              disabled={passwordResetLoading}
            >
              {getText("Reset password")}
              {passwordResetLoading && (
                <IconSpinner className="ml-1 h-4 w-4 animate-spin" />
              )}
            </button>
            <div
              hidden={!errorMsg}
              className="border-destructiveBorder bg-destructiveBgDefault text-destructiveText text-bodymd mt-6 rounded-sm border px-4 py-2 text-center"
            >
              {errorMsg}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
